/**
 * Profile-key distribution control message.
 *
 * When a user grants a peer access to their profile, the profile key
 * itself must travel inside an already-established pairwise E2EE envelope.
 * The codec defined here produces the *plaintext that gets encrypted* —
 * the on-the-wire envelope is the standard `kaether:e2ee:v1:` form, so
 * the server cannot tell a profile-key distribution from any other chat
 * message by length or prefix. The receiver decrypts the envelope,
 * recognises the prefix, validates the key length, and records the key in
 * their local ProfileKeyStore.
 *
 * Wire format of the *plaintext that gets encrypted*:
 *
 *   kaether:profile:share:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "schema": 1, "profileKey": "<base64 32 bytes>",
 *     "ownerUserId": "...", "createdAt": 1700000000 }
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ProfileKeyDistribution.swift`
 * byte-for-byte.
 */

import { ProfileKeyError } from "./ProfileKey";

// ---------------------------------------------------------------------------
// Payload value type
// ---------------------------------------------------------------------------

export interface ProfileKeyDistributionPayload {
  readonly schema: number;
  /** Raw 32-byte profile key encoded as standard base64. */
  readonly profileKey: Uint8Array;
  readonly ownerUserId: string;
  readonly createdAt: number; // Unix seconds (Int64 on iOS)
}

export const DISTRIBUTION_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Wire prefix
// ---------------------------------------------------------------------------

/** Wire prefix for distribution plaintext: "kaether:profile:share:v1:" */
export const DISTRIBUTION_PREFIX = "kaether:profile:share:v1:";

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

interface RawPayload {
  schema: number;
  profileKey: string;
  ownerUserId: string;
  createdAt: number;
}

export const ProfileKeyDistributionCodec = {
  /** True when `s` starts with the distribution prefix. */
  isDistribution(s: string): boolean {
    return s.startsWith(DISTRIBUTION_PREFIX);
  },

  /**
   * Encodes a distribution payload into the wire string.
   * Throws if profileKey is not exactly 32 bytes.
   */
  encode(p: ProfileKeyDistributionPayload): string {
    if (p.profileKey.byteLength !== 32) {
      throw new ProfileKeyError(
        "invalidKeyLength",
        `profileKey must be 32 bytes, got ${p.profileKey.byteLength}`,
      );
    }
    const raw: RawPayload = {
      schema: p.schema,
      profileKey: uint8ArrayToBase64(p.profileKey),
      ownerUserId: p.ownerUserId,
      createdAt: p.createdAt,
    };
    const json = JSON.stringify(raw);
    return DISTRIBUTION_PREFIX + base64UrlEncode(new TextEncoder().encode(json));
  },

  /**
   * Decodes a wire string produced by `encode`.
   * Throws ProfileKeyError for any validation failure.
   */
  decode(s: string): ProfileKeyDistributionPayload {
    if (!s.startsWith(DISTRIBUTION_PREFIX)) {
      throw new ProfileKeyError(
        "malformedBlob",
        "not a profile-key distribution",
      );
    }
    const b64 = s.slice(DISTRIBUTION_PREFIX.length);
    let jsonBytes: Uint8Array;
    try {
      jsonBytes = base64UrlDecode(b64);
    } catch {
      throw new ProfileKeyError("malformedBlob", "invalid base64url");
    }

    let raw: RawPayload;
    try {
      raw = JSON.parse(new TextDecoder().decode(jsonBytes)) as RawPayload;
    } catch {
      throw new ProfileKeyError("malformedBlob", "JSON decode failed");
    }

    if (raw.schema !== DISTRIBUTION_SCHEMA_VERSION) {
      throw new ProfileKeyError(
        "unsupportedSchema",
        `unsupported schema: ${raw.schema}`,
      );
    }

    let keyBytes: Uint8Array;
    try {
      keyBytes = base64ToUint8Array(raw.profileKey);
    } catch {
      throw new ProfileKeyError("malformedBlob", "invalid base64 in profileKey");
    }

    if (keyBytes.byteLength !== 32) {
      throw new ProfileKeyError(
        "invalidKeyLength",
        `profileKey must be 32 bytes, got ${keyBytes.byteLength}`,
      );
    }

    if (!raw.ownerUserId || raw.ownerUserId.length === 0) {
      throw new ProfileKeyError("malformedBlob", "empty ownerUserId");
    }

    return {
      schema: raw.schema,
      profileKey: keyBytes,
      ownerUserId: raw.ownerUserId,
      createdAt: raw.createdAt,
    };
  },

  /**
   * Convenience: returns null for non-distribution strings.
   * Throws only for strings that claimed to be distributions but are malformed.
   */
  tryDecode(s: string): ProfileKeyDistributionPayload | null {
    if (!ProfileKeyDistributionCodec.isDistribution(s)) return null;
    return ProfileKeyDistributionCodec.decode(s);
  },
};

// ---------------------------------------------------------------------------
// Base64 / Base64url helpers (no padding, URL-safe)
// ---------------------------------------------------------------------------

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function base64ToUint8Array(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function base64UrlEncode(bytes: Uint8Array): string {
  return uint8ArrayToBase64(bytes)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

function base64UrlDecode(s: string): Uint8Array {
  let padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (padded.length % 4)) % 4;
  padded += "=".repeat(pad);
  return base64ToUint8Array(padded);
}
